import React, { useState, useEffect } from 'react';
import { Target, Plus, Edit2, Trash2, Trophy, Clock, Gift, Users, X, Save } from 'lucide-react';
import api from '../../utils/api';
import toast from 'react-hot-toast';

const RANK_OPTIONS = [
  { value: 'starter', label: 'Starter' },
  { value: 'silver', label: 'Silver' },
  { value: 'gold', label: 'Gold' },
  { value: 'diamond', label: 'Diamond' },
  { value: 'the_king', label: 'The King' },
];

const AdminRankCompetitions = () => {
  const [competitions, setCompetitions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingComp, setEditingComp] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    start_date: '',
    end_date: '',
    target_rank: 'silver',
    reward: 5000,
    reward_description: ''
  });

  useEffect(() => {
    fetchCompetitions();
  }, []);

  const fetchCompetitions = async () => {
    try {
      const res = await api.get('/admin/ranks/competitions');
      setCompetitions(res.data);
    } catch (error) {
      console.error('Failed to fetch competitions:', error);
      toast.error('Failed to load competitions');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.title || !formData.start_date || !formData.end_date) {
      toast.error('Please fill in all required fields');
      return;
    }

    setSubmitting(true);
    try {
      if (editingComp) {
        await api.put(`/admin/ranks/competitions/${editingComp.id}`, formData);
        toast.success('Competition updated');
      } else {
        await api.post('/admin/ranks/competitions', formData);
        toast.success('Competition created');
      }
      setShowForm(false);
      setEditingComp(null);
      resetForm();
      fetchCompetitions();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save competition');
    } finally {
      setSubmitting(false);
    }
  };

  const handleEdit = (comp) => {
    setEditingComp(comp);
    setFormData({
      title: comp.title,
      description: comp.description || '',
      start_date: comp.start_date?.split('T')[0] || '',
      end_date: comp.end_date?.split('T')[0] || '',
      target_rank: comp.target_rank || 'silver',
      reward: comp.reward || 0,
      reward_description: comp.reward_description || ''
    });
    setShowForm(true);
  };

  const handleDelete = async (compId) => {
    if (!window.confirm('Delete this competition?')) return;
    try {
      await api.delete(`/admin/ranks/competitions/${compId}`);
      toast.success('Competition deleted');
      fetchCompetitions();
    } catch (error) {
      toast.error('Failed to delete');
    }
  };

  const toggleActive = async (comp) => {
    try {
      await api.put(`/admin/ranks/competitions/${comp.id}`, { is_active: !comp.is_active });
      toast.success(comp.is_active ? 'Competition deactivated' : 'Competition activated');
      fetchCompetitions();
    } catch (error) {
      toast.error('Failed to update');
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      start_date: '',
      end_date: '',
      target_rank: 'silver',
      reward: 5000,
      reward_description: ''
    });
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleDateString('en-IN', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  return (
    <div className="min-h-screen pt-24 pb-16 px-6" data-testid="admin-rank-competitions">
      <div className="max-w-6xl mx-auto">
        <div className="flex items-center justify-between mb-8">
          <h1 className="text-4xl font-bold flex items-center gap-3">
            <Target className="w-10 h-10 text-orange-500" />
            Rank Competitions
          </h1>
          <button
            onClick={() => { resetForm(); setEditingComp(null); setShowForm(true); }}
            className="btn-primary flex items-center gap-2"
            data-testid="create-competition-btn"
          >
            <Plus className="w-5 h-5" />
            Create Competition
          </button>
        </div>

        {/* Form Modal */}
        {showForm && (
          <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
            <div className="bg-[#1a1a1a] rounded-2xl p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold">
                  {editingComp ? 'Edit Competition' : 'Create Competition'}
                </h2>
                <button onClick={() => setShowForm(false)} className="text-muted-foreground hover:text-white">
                  <X className="w-6 h-6" />
                </button>
              </div>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm mb-1">Title *</label>
                  <input
                    type="text"
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-xl"
                    placeholder="e.g. Silver Rush - February Challenge"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm mb-1">Description</label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-xl"
                    rows={3}
                    placeholder="Describe the competition rules and how to participate..."
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm mb-1">Start Date *</label>
                    <input
                      type="date"
                      value={formData.start_date}
                      onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                      className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-xl"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm mb-1">End Date *</label>
                    <input
                      type="date"
                      value={formData.end_date}
                      onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                      className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-xl"
                      required
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm mb-1">Target Rank *</label>
                    <select
                      value={formData.target_rank}
                      onChange={(e) => setFormData({ ...formData, target_rank: e.target.value })}
                      className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-xl"
                    >
                      {RANK_OPTIONS.map(rank => (
                        <option key={rank.value} value={rank.value}>{rank.label}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm mb-1">Reward Amount (₹) *</label>
                    <input
                      type="number"
                      value={formData.reward}
                      onChange={(e) => setFormData({ ...formData, reward: parseFloat(e.target.value) || 0 })}
                      className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-xl"
                      min="0"
                      required
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm mb-1">Reward Description</label>
                  <input
                    type="text"
                    value={formData.reward_description}
                    onChange={(e) => setFormData({ ...formData, reward_description: e.target.value })}
                    className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-xl"
                    placeholder="e.g. Cash prize + Free renewal"
                  />
                </div>

                <div className="flex gap-3 pt-4">
                  <button
                    type="button"
                    onClick={() => setShowForm(false)}
                    className="flex-1 px-4 py-3 bg-white/10 rounded-xl hover:bg-white/20"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={submitting}
                    className="flex-1 btn-primary flex items-center justify-center gap-2"
                  >
                    <Save className="w-5 h-5" />
                    {submitting ? 'Saving...' : 'Save Competition'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Competitions List */}
        {loading ? (
          <div className="text-center py-8">Loading...</div>
        ) : competitions.length === 0 ? (
          <div className="glass-card p-8 text-center">
            <Target className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
            <p className="text-muted-foreground">No competitions yet</p>
            <p className="text-sm text-muted-foreground mt-2">
              Create rank competitions with timelines to motivate users to achieve higher ranks!
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {competitions.map((comp) => {
              const isExpired = new Date(comp.end_date) < new Date();
              return (
                <div 
                  key={comp.id} 
                  className={`glass-card p-6 ${!comp.is_active ? 'opacity-60' : ''}`}
                  data-testid={`competition-${comp.id}`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className="text-xl font-bold">{comp.title}</h3>
                        {comp.is_active && !isExpired ? (
                          <span className="text-xs bg-green-500/20 text-green-500 px-2 py-1 rounded">Active</span>
                        ) : isExpired ? (
                          <span className="text-xs bg-red-500/20 text-red-500 px-2 py-1 rounded">Ended</span>
                        ) : (
                          <span className="text-xs bg-yellow-500/20 text-yellow-500 px-2 py-1 rounded">Inactive</span>
                        )}
                        <span className="text-xs bg-purple-500/20 text-purple-500 px-2 py-1 rounded uppercase">
                          {comp.target_rank}
                        </span>
                      </div>
                      <p className="text-muted-foreground text-sm mb-3">{comp.description}</p>
                      
                      <div className="flex flex-wrap gap-4 text-sm">
                        <div className="flex items-center gap-2">
                          <Clock className="w-4 h-4 text-blue-500" />
                          <span>{formatDate(comp.start_date)} - {formatDate(comp.end_date)}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <Gift className="w-4 h-4 text-yellow-500" />
                          <span className="font-bold text-yellow-500">₹{comp.reward?.toLocaleString()}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <Users className="w-4 h-4 text-green-500" />
                          <span>{comp.participants || 0} participants</span>
                        </div>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => toggleActive(comp)}
                        className={`px-3 py-2 rounded-lg text-sm ${
                          comp.is_active 
                            ? 'bg-yellow-500/20 text-yellow-500 hover:bg-yellow-500/30' 
                            : 'bg-green-500/20 text-green-500 hover:bg-green-500/30'
                        }`}
                      >
                        {comp.is_active ? 'Deactivate' : 'Activate'}
                      </button>
                      <button
                        onClick={() => handleEdit(comp)}
                        className="p-2 bg-blue-500/20 text-blue-500 rounded-lg hover:bg-blue-500/30"
                      >
                        <Edit2 className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(comp.id)}
                        className="p-2 bg-red-500/20 text-red-500 rounded-lg hover:bg-red-500/30"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminRankCompetitions;
